/**
 * Offline capture queue — PWA building block.
 *
 * Captured media (video blob / image blobs + audio) are persisted to IndexedDB so a recording
 * survives a page reload / loss of connectivity. When online, `flushQueue()` walks pending
 * items and runs the uploads.init -> PUT -> complete handshake. This is a robust skeleton:
 * the network handshake is best-effort and never blocks the UI.
 */
import { uploads, isAuthed, ApiError, withUploadRetry, type CreateManualInput } from "./api.js";

const DB_NAME = "tebiki-offline";
const STORE = "capture-queue";
/** Recorded source videos kept by manualId so the keyframe picker can extract without re-fetch. */
const VIDEO_STORE = "source-video";
const DB_VERSION = 2;

export type QueueStatus = "pending" | "uploading" | "done" | "error";

export interface QueueItem {
  id: string;
  createdAt: number;
  status: QueueStatus;
  /** human label shown in the inbox / upload queue */
  filename: string;
  contentType: string;
  source: CreateManualInput;
  blob: Blob;
  /** server manual id once created */
  manualId?: string;
  progress?: number; // 0..1
  error?: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(VIDEO_STORE)) {
        db.createObjectStore(VIDEO_STORE, { keyPath: "manualId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txIn<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return txIn(STORE, mode, fn);
}

const uid = () => `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export async function enqueueCapture(input: {
  filename: string;
  contentType: string;
  source: CreateManualInput;
  blob: Blob;
  /** server draft already created by a failed direct upload — reused on flush */
  manualId?: string;
  /** why it is waiting (shown in the 受信箱 queue) */
  error?: string;
}): Promise<QueueItem> {
  const item: QueueItem = {
    id: uid(),
    createdAt: Date.now(),
    status: "pending",
    progress: 0,
    ...input,
  };
  await tx("readwrite", (s) => s.put(item));
  return item;
}

export async function listQueue(): Promise<QueueItem[]> {
  try {
    const all = await tx<QueueItem[]>("readonly", (s) => s.getAll() as IDBRequest<QueueItem[]>);
    return all.sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

export async function removeQueued(id: string): Promise<void> {
  await tx("readwrite", (s) => s.delete(id) as unknown as IDBRequest<unknown>);
}

async function patch(id: string, patch: Partial<QueueItem>): Promise<void> {
  const existing = await tx<QueueItem | undefined>(
    "readonly",
    (s) => s.get(id) as IDBRequest<QueueItem | undefined>,
  );
  if (!existing) return;
  await tx("readwrite", (s) => s.put({ ...existing, ...patch }));
}

/**
 * Attempt to upload a single queued capture. Resolves with the created manual id on success.
 * On failure it marks the item `error` and rethrows so callers can surface it; the blob stays
 * in the queue for a later retry.
 */
/** Human-readable upload failure reason (stored on the item, shown in the 受信箱 queue). */
function friendlyUploadError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "ログインの有効期限が切れています。ログインし直して再送信してください。";
    if (e.status === 413) return "ファイルが大きすぎて送信できません。";
    return `送信に失敗しました（HTTP ${e.status}）。もう一度お試しください。`;
  }
  return "送信できませんでした。通信環境を確認してもう一度お試しください。";
}

export async function uploadQueued(item: QueueItem): Promise<string> {
  await patch(item.id, { status: "uploading", progress: 0.1, error: undefined });
  try {
    // uploads.init creates the draft manual + media (do NOT manuals.create separately —
    // that left untitled "（無題のマニュアル）" orphans). Current handshake: init -> PUT -> complete.
    // On a RETRY, reuse the manualId from the previous attempt so a failed flush doesn't spawn a
    // fresh "（撮影中のマニュアル）" draft every time (that produced dozens of orphan drafts).
    const kind = item.source.sourceType === "video" ? "video" : "image";
    let init;
    try {
      init = await uploads.init({
        sourceType: item.source.sourceType,
        files: [{ kind, mimeType: item.contentType }],
        ...(item.manualId ? { manualId: item.manualId } : {}),
      });
    } catch (e) {
      // A STALE manualId (draft deleted / captured under another account・org) 400/403/404s on
      // every retry and wedges the item forever. Drop the stale id and start a fresh draft.
      if (item.manualId && e instanceof ApiError && [400, 403, 404].includes(e.status)) {
        init = await uploads.init({
          sourceType: item.source.sourceType,
          files: [{ kind, mimeType: item.contentType }],
        });
      } else {
        throw e;
      }
    }
    const manualId = init.manualId;
    await patch(item.id, { manualId, progress: 0.3 });
    const target = init.uploads.find((u) => u.kind === kind) ?? init.uploads[0];
    if (target) {
      // Real transfer progress (0.3 → 0.85): large blobs upload as chunks, so the bar moves
      // per-part instead of sitting at 30% for the whole transfer.
      await uploads.put(target.mediaId, item.blob, (frac) => {
        void patch(item.id, { progress: 0.3 + frac * 0.55 });
      });
      await patch(item.id, { progress: 0.85 });
      await uploads.complete(target.mediaId, { manualId });
    }
    await patch(item.id, { status: "done", progress: 1 });
    return manualId;
  } catch (e) {
    await patch(item.id, { status: "error", error: friendlyUploadError(e) });
    throw e;
  }
}

/** Outcome of sending a finished video: uploaded now, parked in the offline queue, or neither. */
export type SendVideoResult =
  | { status: "uploaded"; manualId: string }
  | { status: "queued"; manualId: string }
  /**
   * Upload failed AND the take could not be queued — it exists only in memory, so keep it on
   * screen. `manualId` is the server draft already created (if any): pass it back on retry so
   * 「もう一度送る」 reuses the draft instead of minting another orphan.
   */
  | { status: "failed"; error: string; manualId: string | null };

/** Above this, skip the local source-video cache after upload (see sendVideoOrQueue). */
const SOURCE_VIDEO_CACHE_MAX_BYTES = 60 * 1024 * 1024;

/** Errors a later retry can never fix — don't park these in the queue, tell the user now. */
const PERMANENT_UPLOAD_STATUSES = [400, 403, 413];

/** `capture_<ts>.<ext>` from the blob's container (mp4 on iOS Safari, webm elsewhere). */
export function captureFilename(blob: Blob): string {
  const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
  return `capture_${Date.now()}.${ext}`;
}

/**
 * Upload a finished video (init → PUT → complete); on a transient failure park it in the offline
 * queue. Unlike the old inline flow this never swallows a failed enqueue: a long recording
 * (250MB+) can exceed IndexedDB quota on iOS, and navigating away then silently lost the take.
 * On success the blob is cached under the manual id for the keyframe picker; the queued path
 * skips that (the queue item already holds the same bytes, and a local_ id never becomes a manual).
 */
export async function sendVideoOrQueue(
  blob: Blob,
  opts: {
    /** display name for the queue; defaults to captureFilename(blob) */
    filename?: string;
    /** draft manual from a previous failed attempt, to reuse (see SendVideoResult.failed) */
    manualId?: string | null;
    onProgress?: (frac: number) => void;
  } = {},
): Promise<SendVideoResult> {
  const contentType = blob.type || "video/webm";
  const files = [{ kind: "video" as const, mimeType: contentType }];
  let manualId: string | null = opts.manualId ?? null;
  let uploadError: unknown = null;
  try {
    let init;
    try {
      init = await withUploadRetry(() =>
        uploads.init({ sourceType: "video", files, ...(manualId ? { manualId } : {}) }),
      );
    } catch (e) {
      // Stale draft (deleted / other org) — same recovery as uploadQueued: start a fresh one.
      if (manualId && e instanceof ApiError && [400, 403, 404].includes(e.status)) {
        manualId = null;
        init = await withUploadRetry(() => uploads.init({ sourceType: "video", files }));
      } else {
        throw e;
      }
    }
    manualId = init.manualId;
    const target = init.uploads.find((u) => u.kind === "video") ?? init.uploads[0];
    if (!target) throw new Error("upload_target_missing");
    await uploads.put(target.mediaId, blob, opts.onProgress);
    await withUploadRetry(() => uploads.complete(target.mediaId, { manualId: init.manualId }));
    // Cache for the keyframe picker only when small. A 250MB IndexedDB write right after holding
    // the same blob in memory is where iOS Safari runs out of memory (WebKit doubles blob memory
    // when many recorder chunks are joined); the picker already falls back to the Range-served
    // original, so skipping the cache costs only a little extra download.
    if (blob.size <= SOURCE_VIDEO_CACHE_MAX_BYTES) await saveSourceVideo(init.manualId, blob);
    return { status: "uploaded", manualId: init.manualId };
  } catch (e) {
    uploadError = e;
  }
  const permanent = uploadError instanceof ApiError && PERMANENT_UPLOAD_STATUSES.includes(uploadError.status);
  if (!permanent) {
    try {
      await enqueueCapture({
        filename: opts.filename ?? captureFilename(blob),
        contentType,
        source: { sourceType: "video" },
        blob,
        ...(manualId ? { manualId } : {}),
        // A 401 means the session died mid-shift: the item flushes after re-login, and the queue
        // row says why it is waiting in the meantime.
        ...(uploadError instanceof ApiError && uploadError.status === 401
          ? { error: friendlyUploadError(uploadError) }
          : {}),
      });
      return { status: "queued", manualId: `local_${Date.now().toString(36)}` };
    } catch {
      /* IndexedDB unavailable / quota exceeded — fall through to `failed` */
    }
  }
  return { status: "failed", error: friendlyUploadError(uploadError), manualId };
}

/** Outcome of a queue flush — lets callers give honest feedback instead of failing silently. */
export interface FlushResult {
  sent: number;
  failed: number;
  /** why nothing was attempted (offline / no session); null when a flush actually ran */
  blocked: "offline" | "auth" | null;
}

/** Flush all pending items when online. Never throws. */
export function flushQueue(): Promise<FlushResult> {
  // Single-flight: the auto-flush ("online" event / app load) and a manual 今すぐ送信 can fire
  // together, and two interleaved flushes uploaded the same item TWICE (duplicate 139MB objects
  // + duplicate jobs in production). Concurrent callers share one run.
  if (flushInFlight) return flushInFlight;
  flushInFlight = doFlush().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

let flushInFlight: Promise<FlushResult> | null = null;

async function doFlush(): Promise<FlushResult> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { sent: 0, failed: 0, blocked: "offline" };
  }
  // Without a session, background uploads would only 401 — leave captures queued until the user
  // logs in (the next "online"/login auto-flush will pick them up).
  if (!isAuthed()) return { sent: 0, failed: 0, blocked: "auth" };
  const items = await listQueue();
  let sent = 0;
  let failed = 0;
  for (const item of items) {
    if (item.status === "done") continue;
    try {
      await uploadQueued(item);
      sent += 1;
    } catch {
      failed += 1; // stays in queue; will retry on next flush
    }
  }
  return { sent, failed, blocked: null };
}

// ---------- source-video cache (for the keyframe picker) ----------

interface SourceVideoRecord {
  manualId: string;
  blob: Blob;
  contentType: string;
  createdAt: number;
}

/**
 * Persist the recorded source video under a manualId so the keyframe picker can extract frames
 * locally without re-downloading. Best-effort: IndexedDB may be unavailable. Replaces any prior
 * record for the same manual.
 */
export async function saveSourceVideo(manualId: string, blob: Blob): Promise<void> {
  if (!manualId) return;
  const record: SourceVideoRecord = {
    manualId,
    blob,
    contentType: blob.type || "video/webm",
    createdAt: Date.now(),
  };
  try {
    await txIn(VIDEO_STORE, "readwrite", (s) => s.put(record));
  } catch {
    /* IndexedDB unavailable — picker will fall back to the Range-served originalVideoUrl */
  }
}

/** Read a cached source video blob for a manual, or null if none / IndexedDB unavailable. */
export async function getSourceVideo(manualId: string): Promise<Blob | null> {
  if (!manualId) return null;
  try {
    const rec = await txIn<SourceVideoRecord | undefined>(
      VIDEO_STORE,
      "readonly",
      (s) => s.get(manualId) as IDBRequest<SourceVideoRecord | undefined>,
    );
    return rec?.blob ?? null;
  } catch {
    return null;
  }
}

/** Drop a cached source video once it's no longer needed (e.g. after picker confirm). */
export async function removeSourceVideo(manualId: string): Promise<void> {
  if (!manualId) return;
  try {
    await txIn(VIDEO_STORE, "readwrite", (s) => s.delete(manualId) as unknown as IDBRequest<unknown>);
  } catch {
    /* best-effort */
  }
}

/** Register listeners that flush the queue when connectivity returns. Returns an unsubscribe. */
export function startAutoFlush(): () => void {
  const handler = () => void flushQueue();
  if (typeof window !== "undefined") {
    window.addEventListener("online", handler);
    // opportunistic first flush
    void flushQueue();
  }
  return () => {
    if (typeof window !== "undefined") window.removeEventListener("online", handler);
  };
}
